import {throwError as observableThrowError,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import {JsonService} from "../../../services/core/json.service";
import {WarehouseApiResources} from "./warehouse.constants";

@Injectable({
  providedIn: 'root'
})
export class WarehouseDataService {
  private baseURL;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = WarehouseApiResources;
  }

  list(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  detail(id:any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  update(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  delete(id:any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE warehouses/delete/multiple with { warehouse_ids: [...] }
  deleteMultiple(warehouseIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { warehouse_ids: warehouseIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected warehouses can be deleted (and why not, for the rest)
  deleteAvailability(warehouseIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { warehouse_ids: warehouseIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  onChangeStatus(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  copyWarehouse(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.copy}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Import History Methods
  getImportHistory(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}`;
    let httpParams = new HttpParams();
    
    // Always add warehouse module_id
    httpParams = httpParams.set('module_id', '17');
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportHistoryDetails(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  deleteImportHistory(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportStats(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/stats`;
    let httpParams = new HttpParams();
    
    // Always add warehouse module_id
    httpParams = httpParams.set('module_id', '17');
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Enhanced Import Methods
  downloadSampleFile(): Observable<any> {
    const endPoint = `${this.baseURL.downloadSample}`;
    return this.http.get(endPoint, { responseType: 'blob' })
      .pipe(catchError(this.errorHandlerMessage));
  }

  uploadImportFile(file: File): Observable<any> {
    const endPoint = `${this.baseURL.uploadImport}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(endPoint, formData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  validateImportData(data: any): Observable<any> {
    const endPoint = `${this.baseURL.validateImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  executeImport(data: any): Observable<any> {
    const endPoint = `${this.baseURL.executeImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // ==========================================
  // Zone Management Methods
  // ==========================================

  /**
   * Get zone templates
   */
  getZoneTemplates(): Observable<any> {
    const endPoint = `${this.baseURL.zoneTemplates}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get all zones for a warehouse
   */
  getZonesByWarehouse(warehouseId: any): Observable<any> {
    const endPoint = `${this.baseURL.zones}/${warehouseId}/zones`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get zones with visual layout (slots + items)
   * FLOW 2: Zone/Slot Visual View
   */
  getZonesVisualLayout(warehouseId: any): Observable<any> {
    const endPoint = `${this.baseURL.zonesVisual}/${warehouseId}/zones/visual`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Create new zone
   */
  createZone(warehouseId: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.createZone}/${warehouseId}/zones`;
    return this.http.post<any>(endPoint, model)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get zone details
   */
  getZone(id: any): Observable<any> {
    const endPoint = `${this.baseURL.zoneDetail}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Update zone
   */
  updateZone(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateZone}/${id}`;
    return this.http.put<any>(endPoint, model)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Delete zone
   */
  deleteZone(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteZone}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get slot details
   */
  getSlotDetails(zoneId: any, slotId: any): Observable<any> {
    const endPoint = `${this.baseURL.slotDetail}/${zoneId}/slots/${slotId}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // ==========================================
  // Floor Management Methods
  // ==========================================

  /**
   * Get all floors for a warehouse
   */
  getFloorsByWarehouse(warehouseId: any): Observable<any> {
    const endPoint = `${this.baseURL.floors}/${warehouseId}/floors`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Search floors (autocomplete)
   */
  searchFloors(query: string, warehouseId: any): Observable<any> {
    const endPoint = `${this.baseURL.floorSearch}`;
    let httpParams = new HttpParams();
    httpParams = httpParams.set('q', query);
    httpParams = httpParams.set('warehouse_id', warehouseId);

    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Find or create floor
   */
  findOrCreateFloor(floorName: string, warehouseId: any): Observable<any> {
    const endPoint = `${this.baseURL.floorFindOrCreate}`;
    return this.http.post<any>(endPoint, { floor_name: floorName, warehouse_id: warehouseId })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // ==========================================
  // Inventory Management Methods
  // ==========================================

  /**
   * Add material to inventory (FLOW 4)
   */
  addInventory(model: any): Observable<any> {
    const endPoint = `${this.baseURL.inventoryCreate}`;
    return this.http.post<any>(endPoint, model)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Update an inventory item's master data — Edit Item (row-actions
   * Action 4). Routes to `PUT /api/v1/warehouse/inventory/{id}`,
   * which is validated against UpdateInventoryRequest on the backend.
   * Quantity, location, and warehouse are never accepted by this
   * endpoint — those flows exist separately (Quick Adjust, Move,
   * warehouse assignment).
   */
  updateInventoryItem(id: number, model: any): Observable<any> {
    const endPoint = `${this.baseURL.inventoryCreate}/${id}`;
    return this.http.put<any>(endPoint, model)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get inventory items (FLOW 5 & 6)
   * Properly handles array parameters for Laravel (key[] format)
   */
  getInventoryItems(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.inventory}`;
    let httpParams = new HttpParams();

    Object.keys(params).forEach(key => {
      const value = params[key];

      if (value !== null && value !== undefined) {
        // Handle arrays - Laravel needs key[] format
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item: any) => {
            httpParams = httpParams.append(`${key}[]`, item);
          });
        } else if (!Array.isArray(value)) {
          // Handle regular values (not arrays)
          httpParams = httpParams.set(key, value);
        }
        // Skip empty arrays
      }
    });

    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Search products for inventory
   */
  searchProducts(searchParams: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.productSearch}`;
    return this.http.post<any>(endPoint, searchParams)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get all products
   */
  getProducts(searchParams: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.productList}`;
    return this.http.post<any>(endPoint, searchParams)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get all locations for a material (FLOW 8: Multi-Location Support)
   */
  getMaterialLocations(materialId: number, warehouseId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventoryMaterialLocations}/${materialId}/locations`;
    let httpParams = new HttpParams().set('warehouse_id', warehouseId.toString());
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Adjust inventory stock (FLOW 9: Adjust Inventory Quantities)
   */
  adjustInventory(inventoryId: number, adjustmentData: any): Observable<any> {
    const endPoint = `${this.baseURL.inventoryAdjust}/${inventoryId}/adjust`;
    return this.http.post<any>(endPoint, adjustmentData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get all items in a bin location (FLOW 11: Cycle Counting)
   */
  getBinItems(binCode: string, warehouseId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventoryBinItems}`;
    let httpParams = new HttpParams()
      .set('bin_code', binCode)
      .set('warehouse_id', warehouseId.toString());
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Process cycle count result (FLOW 11: Cycle Counting)
   */
  processCycleCount(countData: any): Observable<any> {
    const endPoint = `${this.baseURL.inventoryCycleCount}`;
    return this.http.post<any>(endPoint, countData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get reservations for an inventory item (FLOW 12: Reservations Management)
   */
  getInventoryReservations(inventoryId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventoryReservations}/${inventoryId}/reservations`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get batch records for a material (FLOW 13: Batch/Lot Tracking)
   */
  getMaterialBatches(materialId: number, warehouseId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventoryBatches}/${materialId}/batches`;
    let httpParams = new HttpParams().set('warehouse_id', warehouseId.toString());
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Adjust batch quantity (FLOW 14: Enhanced Batch Management)
   */
  adjustBatchQuantity(batchId: string, adjustmentQuantity: number, reason: string): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/batch/${batchId}/adjust`;
    const payload = {
      adjustment_quantity: adjustmentQuantity,
      reason: reason
    };
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Split batch into multiple batches (FLOW 14)
   */
  splitBatch(batchId: string, splits: Array<{quantity: number, batch_number: string}>): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/batch/${batchId}/split`;
    const payload = { splits };
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Transfer batch to different location (FLOW 14)
   */
  transferBatch(batchId: string, toZoneId: number, toBinCode: string, quantity?: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/batch/${batchId}/transfer`;
    const payload = {
      to_zone_id: toZoneId,
      to_bin_code: toBinCode,
      quantity: quantity
    };
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get batch details with locations and history (FLOW 14)
   */
  getBatchDetails(batchId: string): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/batch/${batchId}/details`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Release a reservation (FLOW 15: Enhanced Reservation Management)
   */
  releaseReservation(reservationId: string, reason?: string): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/reservation/${reservationId}/release`;
    const payload = { reason: reason ?? null };
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Extend a reservation date (FLOW 15)
   */
  extendReservation(reservationId: string, newDate: string): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/reservation/${reservationId}/extend`;
    const payload = { new_date: newDate };
    return this.http.put<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Transfer a reservation (FLOW 15)
   */
  transferReservation(reservationId: string, toJobId?: number, toPickupId?: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/reservation/${reservationId}/transfer`;
    const payload = {
      to_job_id: toJobId || null,
      to_pickup_id: toPickupId || null
    };
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Fulfill a reservation (FLOW 15)
   */
  fulfillReservation(reservationId: string, fulfilledQuantity: number, notes?: string): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/reservation/${reservationId}/fulfill`;
    const payload = {
      fulfilled_quantity: fulfilledQuantity,
      notes: notes || null
    };
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Create a new reservation (FLOW 15)
   */
  createReservation(data: {
    inventory_master_id: number,
    job_id?: number,
    pickup_id?: number,
    quantity: number,
    unit?: string,
    needed_by: string,
    notes?: string
  }): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/reservation`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Generate QR code for inventory item (FLOW 16)
   */
  generateQRCode(inventoryId: number, options: { size?: number, format?: 'png' | 'svg' } = {}): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/generate-qr`;
    return this.http.post<any>(endPoint, options)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get QR code data for inventory item (FLOW 16)
   */
  getQRData(inventoryId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/qr-data`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // ==========================================
  // FLOW 23 — Summary Metrics & Badges
  // ==========================================

  /**
   * Get warehouse-wide inventory metrics (total, low stock, value, etc.)
   */
  getInventoryMetrics(warehouseId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/metrics`;
    const params = new HttpParams().set('warehouse_id', warehouseId.toString());
    return this.http.get<any>(endPoint, { params })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // ==========================================
  // FLOW 22 — Slot Analysis
  // ==========================================

  /**
   * Get slot analysis (zone distribution + storage metrics + consolidation)
   * for an inventory item.
   */
  getSlotAnalysis(inventoryId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/slot-analysis`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // ==========================================
  // FLOW 21 — Documents Tab
  // ==========================================

  /**
   * List documents attached to an inventory item.
   */
  getInventoryDocuments(inventoryId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/documents`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Upload a document against an inventory item.
   * The file must be sent under the `attachment` FormData field.
   */
  uploadInventoryDocument(inventoryId: number, file: File): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/documents`;
    const formData = new FormData();
    formData.append('attachment', file, file.name);
    return this.http.post<any>(endPoint, formData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Delete a document from an inventory item.
   */
  deleteInventoryDocument(inventoryId: number, documentId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/documents/${documentId}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Request a short-lived signed S3 URL for downloading a document.
   */
  getInventoryDocumentDownloadUrl(inventoryId: number, documentId: number): Observable<any> {
    const endPoint = `${this.baseURL.inventory}/${inventoryId}/documents/${documentId}/download-url`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
